import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import SearchBar from "../../components/SearchBar.js";
import AddOrEmptyTile from "../../components/AddOrEmptyTile.js";
import PosterTile from "../../components/PosterTile.js";
import SortableItem from "../../components/SortableItem.js";
import useApi from "../../hooks/useApi.js";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import ProfileCollectionMetadata, {
  getFavouritesDescription,
} from "./ProfileCollectionMetadata.js";
import { motion } from "framer-motion";
import { useDetailNavigation } from "../../hooks/useDetailNavigation.js";
import { useScaleGestures } from "../../hooks/useScaleGestures.js";
import { useDebounce } from "../../hooks/useDebounce.js";
import { useAuth } from "../../context/authContext.js";
import { getContentLabel } from "../../utils/utils.js";
import { useListMutations } from "../../hooks/useCustomQuery.js";
import { getProfileKind } from "../../utils/listSchema.js";
import EmptySectionState from "../../components/EmptySectionState.js";

// A title slot + 5 posters in one row on sm+ (title cell + 5 posters in thirds
// on xs). ProfileStandouts sizes its posters from this grid — keep them in step.
const METADATA_COLS = { xs: 4, sm: 2 };
const POSTER_COLS = { xs: 4, sm: 2 };
const GRID_SPACING = { xs: 2, sm: 2.5 };

/**
 * A profile pick collection (the all-time top 5) as a card of posters the owner
 * can add to, replace, remove and drag to reorder. `grouped` titles it by its
 * content type instead, for use under a shared heading. `finishedCount` (the
 * user's finished total for the type) feeds the "favourites from" description.
 * Visitors never see an empty one — the profile hides it (routes/Profile.js).
 *
 * `collapseWhenEmpty`: an empty one is a single row — the description beside
 * an empty state with an add prompt — rather than five add tiles, for the
 * owner's All view, where it's one of several stacked top 5s.
 */
const ProfileCollection = ({
  list,
  fullScreen,
  readOnly,
  grouped,
  finishedCount,
  collapseWhenEmpty = false,
}) => {
  const { fetchSeasons } = useApi();
  const { user } = useAuth();
  const { list_id, size, content_type } = list;
  // Derive profile kind from tags for layout/grid sizing decisions
  const profileKind = getProfileKind(list);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [itemToReplace, setItemToReplace] = useState(null);
  const [action, setAction] = useState("");
  const [draggableItem, setDraggableItem] = useState(null);
  const { openContent } = useDetailNavigation();
  // Tiles always have PosterTile's long press (list actions for the owner, the
  // status overlay for visitors) — hover only, as a tap press-in would mute its
  // pop. Inside SortableItem so it never shares an element with dnd-kit's drag
  // transform.
  const hoverGestures = useScaleGestures({ tap: false });
  const { addToList, removeFromList, replaceInList, reorderListAsync } =
    useListMutations({
      origin: list.origin,
      profileKind,
      username: user?.username,
    });

  const [editedList, setEditedList] = useState(list);
  const numberOfItems = size ? size : list.items.length ? list.items.length : 1;
  const [seriesInfo, setSeriesInfo] = useState({ title: "", seasons: [] });

  useEffect(() => {
    // Update state to use the latest data whenever list changes
    setEditedList(list);
  }, [list, numberOfItems]);

  const updateEditedList = (name, value) => {
    setEditedList((prevList) => ({
      ...prevList,
      [name]: typeof value === "function" ? value(prevList[name]) : value,
    }));
  };

  const handleOpenSearchDialog = (item, action) => {
    setOpenSearchDialog(true);
    setItemToReplace(item);
    setAction(action);
  };

  const handleCloseSearchDialog = () => {
    setOpenSearchDialog(false);
    setItemToReplace(null);
    setAction("");
    setSeriesInfo({ title: "", seasons: [] });
  };

  const debouncedReorderListMutation = useDebounce(async (newItems) => {
    await reorderListAsync({ listId: list_id, items: newItems });
  }, 5000); // Debounce with a 5-second delay

  useEffect(() => {
    return () => {
      // Execute pending changes on dismount if collection is closed before debounce delay
      debouncedReorderListMutation.flush();
    };
  }, [debouncedReorderListMutation]);

  const handleSearchbarSelect = async (content_id, content_type) => {
    if (content_type === "tvshow") {
      const { series_title, seasons } = await fetchSeasons(content_id);
      setSeriesInfo({ title: series_title, seasons });
    } else {
      action === "replace" ? handleReplace(content_id) : handleAdd(content_id);
    }
  };

  const handleAdd = useCallback(
    (contentId) => {
      addToList({
        listId: list_id,
        contentId,
      });
      handleCloseSearchDialog();
    },
    [addToList, list_id],
  );

  const handleReplace = useCallback(
    async (newContentId) => {
      await debouncedReorderListMutation.flush();
      replaceInList({
        listId: list_id,
        oldContentId: itemToReplace.content_id,
        newContentId,
      });
      handleCloseSearchDialog();
    },
    [debouncedReorderListMutation, itemToReplace, list_id, replaceInList],
  );

  const handleRemove = async (contentId) => {
    await debouncedReorderListMutation.flush(); // ensure any pending reorders are applied before
    removeFromList({
      listId: list_id,
      contentId,
    });
  };

  useEffect(() => {
    if (seriesInfo.seasons.length === 1 && seriesInfo.seasons[0].content_id) {
      const contentId = seriesInfo.seasons[0].content_id;
      if (action === "replace") {
        handleReplace(contentId);
      } else {
        handleAdd(contentId);
      }
      setSeriesInfo({ title: "", seasons: [] });
    }
  }, [seriesInfo, action, handleAdd, handleReplace]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragStart(event) {
    const { active } = event;
    const matchedItem = editedList.items.find(
      (item) => item.content_id === active.id,
    );
    setDraggableItem(matchedItem);
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDraggableItem(null);
      updateEditedList("items", (currentItems) => {
        const oldIndex = currentItems.findIndex(
          (item) => item.content_id === active.id,
        );
        const newIndex = currentItems.findIndex(
          (item) => item.content_id === over.id,
        );

        const newItems = arrayMove(currentItems, oldIndex, newIndex).map(
          (item, index) => ({
            ...item,
            position: index + 1,
          }),
        );

        debouncedReorderListMutation(newItems); // ensures API call is only made once, x seconds after the last reorder
        return newItems;
      });
    }
  }

  // editedList so it expands as soon as the first pick is added
  const isCollapsed = collapseWhenEmpty && editedList.items.length === 0;

  return (
    <>
      <Card
        elevation={0}
        sx={{
          p: 1.5,
          // Page-coloured with a faint outline: a filled card read as heavy,
          // but the border still groups the title and posters as one unit
          bgcolor: "background.default",
          backgroundImage: "none",
          border: 1,
          borderColor: "divider",
          width: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
        }}
      >
        <Grid container spacing={GRID_SPACING}>
          <Grid item {...METADATA_COLS}>
            {/* editedList so the count follows adds/removes immediately */}
            <ProfileCollectionMetadata
              list={editedList}
              grouped={grouped}
              finishedCount={finishedCount}
              hideDescription={isCollapsed}
            />
          </Grid>

          {isCollapsed ? (
            <Grid item xs>
              {/* The description moves here, beside the empty icon, in
                  place of a message restating it */}
              <EmptySectionState
                message={getFavouritesDescription(
                  editedList,
                  finishedCount ?? 0,
                )}
                actionLabel="Add a favourite"
                onAction={() => handleOpenSearchDialog(null, "add")}
              />
            </Grid>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={editedList.items.map((item) => item.content_id)}
                  strategy={rectSortingStrategy}
                >
                  {editedList.items.map((item) => {
                    return (
                      <Grid key={item.content_id} item {...POSTER_COLS}>
                        <SortableItem
                          key={item.content_id}
                          id={item.content_id}
                        >
                          {({ attributes, listeners }) => (
                            <motion.div {...hoverGestures}>
                              <PosterTile
                                contentId={item.content_id}
                                contentType={editedList.content_type}
                                contentTitle={item.title}
                                imageUrl={item.image_url}
                                overlay={true}
                                onClick={() =>
                                  openContent({
                                    contentId: item.content_id,
                                    contentType: item.content_type,
                                    title: item.title,
                                  })
                                }
                                // Only the owner can edit, so readOnly means a
                                // visitor; your own picks show no status border
                                showStatus={readOnly ? "full" : null}
                                position={
                                  editedList.is_ranked ? item.position : undefined
                                }
                                listActions={
                                  !readOnly
                                    ? [
                                        ...(editedList.items.length > 1
                                          ? [
                                              {
                                                label: "Drag to reorder",
                                                value: "reorder",
                                                icon: (
                                                  <DragIndicatorIcon
                                                    {...attributes}
                                                    {...listeners}
                                                    sx={{
                                                      touchAction: "none",
                                                      cursor: "grab",
                                                    }}
                                                    fontSize="inherit"
                                                  />
                                                ),
                                              },
                                            ]
                                          : []),
                                        {
                                          label: "Replace",
                                          value: "replace",
                                          icon: (
                                            <RefreshIcon fontSize="inherit" />
                                          ),
                                          onClick: () => {
                                            handleOpenSearchDialog(
                                              item,
                                              "replace",
                                            );
                                          },
                                        },
                                        {
                                          label: "Remove",
                                          value: "remove",
                                          icon: <RemoveIcon fontSize="inherit" />,
                                          onClick: () => {
                                            handleRemove(item.content_id);
                                          },
                                        },
                                      ]
                                    : []
                                }
                              />
                            </motion.div>
                          )}
                        </SortableItem>
                      </Grid>
                    );
                  })}
                </SortableContext>

                <DragOverlay>
                  {draggableItem ? (
                    <PosterTile
                      contentId={draggableItem.content_id}
                      contentType={draggableItem.content_type}
                      contentTitle={draggableItem.title}
                      imageUrl={draggableItem.image_url}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>

              {size &&
                Array.from(
                  { length: size - editedList.items.length },
                  (_, index) => (
                    <Grid item key={index} {...POSTER_COLS}>
                      <AddOrEmptyTile
                        onClick={() => handleOpenSearchDialog(null, "add")}
                        readOnly={readOnly}
                      />
                    </Grid>
                  ),
                )}

              {editedList.items.length === 0 && !size && (
                <Grid item {...POSTER_COLS}>
                  <AddOrEmptyTile
                    onClick={() => handleOpenSearchDialog(null, "add")}
                    readOnly={readOnly}
                  />
                </Grid>
              )}

              {fullScreen &&
                [1, 2].includes(editedList.items.length) &&
                Array.from({ length: 3 - editedList.items.length }).map(
                  (_, index) => (
                    <Grid
                      key={`invisible-add-tile-${index}`}
                      item
                      xs={4}
                      sx={{
                        visibility: "hidden", // Makes it invisible but reserves space
                      }}
                    >
                      <AddOrEmptyTile readOnly={readOnly} />
                    </Grid>
                  ),
                )}
            </>
          )}
        </Grid>
      </Card>

      <Dialog
        open={openSearchDialog}
        onClose={handleCloseSearchDialog}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            paddingTop: "8vh",
          },
        }}
        disableRestoreFocus={process.env.NODE_ENV === "development"}
      >
        <DialogContent
          sx={{
            border: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 108,
          }}
        >
          {seriesInfo.seasons.length > 1 ? (
            <Box>
              <Typography variant="body2">
                Select which season of <b>{seriesInfo.title}</b> to add...
              </Typography>
              <Tabs
                value={false} // uncontrolled
                variant="scrollable"
                scrollButtons="auto"
                onChange={(_, contentId) => {
                  action === "replace"
                    ? handleReplace(contentId)
                    : handleAdd(contentId);
                  setSeriesInfo({ title: "", seasons: [] });
                }}
                sx={{
                  maxWidth: "100%",
                  minHeight: 30,
                  mt: 2,
                }}
              >
                {seriesInfo.seasons.map(({ season_number, content_id }) => (
                  <Tab
                    key={content_id}
                    value={content_id}
                    label={
                      season_number === 0 ? "Whole series" : `S${season_number}`
                    }
                    sx={{
                      minWidth: 20,
                      py: 0,
                      minHeight: 30,
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          ) : (
            <SearchBar
              contentType={content_type}
              onSelect={handleSearchbarSelect}
              onClose={handleCloseSearchDialog}
              enableQuickAdd={false}
              clearOnSelect={true}
              title={
                action === "replace" ? (
                  <>
                    Replace <strong>{itemToReplace.title}</strong> with...
                  </>
                ) : (
                  <>
                    Add{" "}
                    {getContentLabel({ type: content_type, lowercase: true })}{" "}
                    to <strong>{list.title}</strong>
                  </>
                )
              }
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileCollection;
